import { useRef, useState } from "react";
import {
  useInteractions,
  useCreateInteraction,
  useUpdateInteraction,
  useDeleteInteraction,
} from "@/hooks/useInteractions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Interaction, InteractionAttachment } from "@/types";
import { formatDate } from "@/lib/utils";
import { AlertCircle, Paperclip, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AttachmentStrip } from "@/components/AttachmentStrip";
import {
  arquivosDoPaste,
  useAttachmentsByClient,
  useDeleteAttachment,
  useUploadAttachment,
  TAMANHO_MAXIMO_BYTES,
  TIPOS_ACEITOS,
} from "@/hooks/useAttachments";

interface Props {
  clientId: string;
  /** Editar/excluir interações (inclusive marcar task como concluída) é admin-only.
   *  Registrar/criar continua liberado pra qualquer usuário aprovado — ver api/src/functions/interactions.ts. */
  isAdmin: boolean;
}

type Draft = Omit<Interaction, "id" | "created_at" | "updated_at">;

const emptyDraft = (clientId: string): Draft => ({
  client_id: clientId,
  interaction_date: null,
  poc_jus: null,
  poc_investor: null,
  backlog: null,
  task: null,
  deadline: null,
  task_completed: false,
});

export function InteractionsSection({ clientId, isAdmin }: Props) {
  const { data: interactions } = useInteractions(clientId);
  const { data: anexos } = useAttachmentsByClient(clientId);
  const create = useCreateInteraction();
  const update = useUpdateInteraction();
  const del = useDeleteInteraction();
  const upload = useUploadAttachment();
  const delAnexo = useDeleteAttachment();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Interaction | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft(clientId));
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Anexos já enviados mas ainda NÃO ligados a uma interação: o upload acontece
  // no momento de colar, antes de salvar. Se o usuário fechar o diálogo sem
  // salvar, ficam órfãos e o job diário da sql/019 os recolhe.
  const [pendentes, setPendentes] = useState<InteractionAttachment[]>([]);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const openCreate = () => {
    setEditTarget(null);
    setDraft(emptyDraft(clientId));
    setPendentes([]);
    setDialogOpen(true);
  };

  const openEdit = (i: Interaction) => {
    setEditTarget(i);
    const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = i;
    setDraft(rest);
    setPendentes([]);
    setDialogOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Anexar: colar, escolher arquivo ou arrastar — os três caem aqui
  // ---------------------------------------------------------------------------
  // Valida tamanho antes de subir, pra dar erro imediato em vez de esperar 10 MB
  // de rede pra receber 413. O tipo NÃO é validado aqui de propósito: a
  // verificação que vale é a de magic bytes no servidor (ver api/src/lib/blob.ts),
  // e duplicar a lista no cliente só criaria duas fontes de verdade pra
  // divergirem. O `accept` do input é conveniência, não controle.
  const anexar = async (arquivos: File[]) => {
    for (const file of arquivos) {
      if (file.size > TAMANHO_MAXIMO_BYTES) {
        toast.error(`${file.name}: acima do limite de ${TAMANHO_MAXIMO_BYTES / 1024 / 1024} MB`);
        continue;
      }
      try {
        // Em interação existente o anexo já nasce ligado (e a API exige admin);
        // em interação nova nasce órfão e é adotado no salvar.
        const criado = await upload.mutateAsync({
          file,
          interactionId: editTarget?.id,
        });
        // Só entra em `pendentes` o que ainda precisa ser adotado no salvar.
        if (!criado.interaction_id) setPendentes((p) => [...p, criado]);
        toast.success(`${file.name} anexado`);
      } catch {
        toast.error(`Erro ao anexar ${file.name}`);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const arquivos = arquivosDoPaste(e);
    if (!arquivos.length) return; // colar texto normal segue o caminho normal
    e.preventDefault();
    void anexar(arquivos);
  };

  const handleDrop = (e: React.DragEvent) => {
    const arquivos = Array.from(e.dataTransfer?.files ?? []);
    if (!arquivos.length) return;
    e.preventDefault();
    void anexar(arquivos);
  };

  const removerAnexo = async (id: string) => {
    try {
      await delAnexo.mutateAsync(id);
      setPendentes((p) => p.filter((a) => a.id !== id));
      toast.success("Anexo removido");
    } catch {
      toast.error("Erro ao remover anexo");
    }
  };

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = async () => {
    try {
      // `attachment_ids` são os órfãos a adotar. A API faz a adoção na MESMA
      // transação do INSERT/UPDATE: ou a interação salva com os anexos, ou nada.
      const attachment_ids = pendentes.map((a) => a.id);
      if (editTarget) {
        await update.mutateAsync({ id: editTarget.id, ...draft, attachment_ids });
        toast.success("Interaction updated");
      } else {
        await create.mutateAsync({ ...draft, attachment_ids });
        toast.success("Interaction created");
      }
      setPendentes([]);
      setDialogOpen(false);
    } catch {
      toast.error("Error saving interaction");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await del.mutateAsync(deleteId);
      toast.success("Interaction removed");
    } catch {
      toast.error("Error removing");
    }
    setDeleteId(null);
  };

  const toggleTaskCompleted = async (i: Interaction, completed: boolean) => {
    try {
      await update.mutateAsync({ id: i.id, task_completed: completed });
    } catch {
      toast.error("Error updating task");
    }
  };

  const busy = create.isPending || update.isPending;
  const sorted = [...(interactions ?? [])].sort((a, b) =>
    (b.interaction_date ?? "").localeCompare(a.interaction_date ?? "")
  );
  const pendingCount = sorted.filter((i) => i.task && !i.task_completed).length;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <CardTitle>Interactions</CardTitle>
            {pendingCount > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertCircle className="h-3 w-3" />
                {pendingCount} pending task{pendingCount > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={openCreate} className="gap-2">
            <Plus className="h-3.5 w-3.5" /> New
          </Button>
        </CardHeader>
        <CardContent>
          {sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground">No interactions recorded.</p>
          ) : (
            <div className="space-y-3">
              {sorted.map((i) => (
                <div key={i.id} className="border rounded-lg p-3 text-sm space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5 flex-1">
                      <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-xs">
                        <span>{formatDate(i.interaction_date)}</span>
                        {i.poc_jus && <span>POC Jus: {i.poc_jus}</span>}
                        {i.poc_investor && <span>POC Inv: {i.poc_investor}</span>}
                        {i.deadline && <span>Deadline: {formatDate(i.deadline)}</span>}
                      </div>
                      {i.backlog && <p className="font-medium">{i.backlog}</p>}
                      {i.task && (
                        <div
                          className={
                            i.task_completed
                              ? "flex items-start gap-2 text-muted-foreground"
                              : "flex items-start gap-2 rounded-md bg-destructive/10 px-2 py-1 text-destructive"
                          }
                        >
                          <Checkbox
                            checked={i.task_completed}
                            disabled={!isAdmin}
                            onCheckedChange={(checked) => toggleTaskCompleted(i, checked === true)}
                            className="mt-0.5"
                          />
                          <span className={i.task_completed ? "line-through" : ""}>{i.task}</span>
                        </div>
                      )}
                      <AttachmentStrip
                        anexos={(anexos ?? []).filter((a) => a.interaction_id === i.id)}
                        onRemover={isAdmin ? removerAnexo : undefined}
                      />
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEdit(i)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setDeleteId(i.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-lg bg-card border-border"
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{editTarget ? "Edit Interaction" : "New Interaction"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={draft.interaction_date ? String(draft.interaction_date).slice(0, 10) : ""}
                  onChange={(e) => set("interaction_date", e.target.value || null)}
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Deadline</Label>
                <Input
                  type="date"
                  value={draft.deadline ? String(draft.deadline).slice(0, 10) : ""}
                  onChange={(e) => set("deadline", e.target.value || null)}
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">POC Jus</Label>
                <Input
                  value={draft.poc_jus ?? ""}
                  onChange={(e) => set("poc_jus", e.target.value || null)}
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">POC Investor</Label>
                <Input
                  value={draft.poc_investor ?? ""}
                  onChange={(e) => set("poc_investor", e.target.value || null)}
                  className="h-8"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Backlog / Notes</Label>
                <Input
                  value={draft.backlog ?? ""}
                  onChange={(e) => set("backlog", e.target.value || null)}
                  className="h-8"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Task</Label>
                <Input
                  value={draft.task ?? ""}
                  onChange={(e) => set("task", e.target.value || null)}
                  className="h-8"
                />
              </div>
              {editTarget && draft.task && (
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox
                    checked={draft.task_completed}
                    onCheckedChange={(checked) => set("task_completed", checked === true)}
                  />
                  <Label className="text-xs font-normal">Task completed</Label>
                </div>
              )}
            </div>
            {/* Anexos. O paste está no DialogContent inteiro, não só nos campos:
                o usuário cola sem pensar em qual campo está focado. */}
            <div className="space-y-2 rounded-md border border-dashed p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Attachments</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5"
                  disabled={upload.isPending}
                  onClick={() => inputArquivo.current?.click()}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {upload.isPending ? "Enviando..." : "Anexar"}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Cole (Ctrl+V), arraste ou escolha o arquivo. JPEG, PNG, WebP ou PDF, até{" "}
                {TAMANHO_MAXIMO_BYTES / 1024 / 1024} MB.
              </p>
              <input
                ref={inputArquivo}
                type="file"
                multiple
                accept={TIPOS_ACEITOS}
                className="hidden"
                onChange={(e) => {
                  void anexar(Array.from(e.target.files ?? []));
                  // Zera o valor pra que escolher o MESMO arquivo de novo
                  // dispare o onChange outra vez.
                  e.target.value = "";
                }}
              />
              <AttachmentStrip
                anexos={
                  editTarget
                    ? (anexos ?? []).filter((a) => a.interaction_id === editTarget.id)
                    : pendentes
                }
                onRemover={removerAnexo}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={busy}>
                {busy ? "Saving..." : editTarget ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove interaction?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
